import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { createBank, deleteBank, getBanks, updateBank } from '../api/bankApi';
import { getBankTransactions } from '../api/bankTransactionApi';

const PageSection = styled.div`
  background: #fff;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const Intro = styled.div`
  max-width: 520px;
  color: #475467;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const SearchInput = styled.input`
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  padding: 9px 16px 9px 36px;
  font-size: 14px;
  min-width: 220px;
  background: #fff url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="%2394a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Ccircle cx="11" cy="11" r="8"/%3E%3Cpath d="m21 21-4.35-4.35"/%3E%3C/svg%3E') no-repeat 12px center;

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
  }
`;

const AddButton = styled.button`
  padding: 10px 16px;
  border-radius: 999px;
  border: none;
  background: #111322;
  color: #f9fafb;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.2);

  &:hover {
    background: #020617;
  }
`;

const Table = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  overflow: hidden;
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.4fr 1.2fr 1fr 1fr;
  background: #f8fafc;
  padding: 14px 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const TableBody = styled.div``;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.4fr 1.2fr 1fr 1fr;
  padding: 18px 20px;
  align-items: center;
  border-top: 1px solid #eef2ff;
  background: #ffffff;

  &:nth-child(even) {
    background: #fdfdfd;
  }
`;

const TableCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BankName = styled.span`
  font-weight: 600;
  color: #0f172a;
`;

const AccountNumber = styled.span`
  font-size: 13px;
  color: #475467;
  letter-spacing: 0.05em;
`;

const AccountHolder = styled.span`
  font-size: 14px;
  color: #0f172a;
  font-weight: 500;
`;

const CurrencyText = styled.span`
  font-weight: 600;
  color: #111322;
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const TotalBalanceCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: #fafbfd;
  color: #0f172a;
  font-weight: 600;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  font-size: 12px;
  color: #0f172a;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
`;

const DangerButton = styled.button`
  padding: 6px 12px;
  border-radius: 999px;
  border: none;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #fecaca;
  }
`;

const CopyButton = styled(ActionButton)`
  border-color: #c7d2fe;
  color: #4338ca;

  &:hover {
    background: #eef2ff;
  }
`;

const EmptyPlaceholder = styled.div`
  padding: 32px;
  border: 1px dashed #cbd5f5;
  border-radius: 18px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`;

const EmptyRow = styled.div`
  padding: 24px;
  text-align: center;
  color: #94a3b8;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 40;
`;

const ModalCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 24px 24px 20px;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.25);
`;

const ModalTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 18px;
  color: #0f172a;
`;

const ModalSubtitle = styled.p`
  margin: 0 0 18px;
  font-size: 13px;
  color: #64748b;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const Label = styled.label`
  font-size: 13px;
  color: #0f172a;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 8px 10px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.2);
  }
`;

const ModalActions = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const SecondaryButton = styled.button`
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
`;

const PrimaryButton = styled.button`
  padding: 8px 14px;
  border-radius: 999px;
  border: none;
  background: #111322;
  color: #f9fafb;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};

  &:hover {
    background: #020617;
  }
`;

const ErrorText = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #dc2626;
`;

const BankPage = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [beginningBalance, setBeginningBalance] = useState('0');
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState(null);
  const [detailBank, setDetailBank] = useState(null);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
    []
  );

  const formatBalance = (value) => currencyFormatter.format(Number(value || 0));

  const bankBalanceMap = useMemo(() => {
    const result = {};

    banks.forEach((bank) => {
      if (bank.id == null) return;
      const beginning = Number(bank.beginningBalance || 0);
      result[bank.id] = Number.isNaN(beginning) ? 0 : beginning;
    });

    transactions.forEach((txn) => {
      const accountId = txn.bankAccountId;
      if (accountId == null) return;

      const amount = Number(txn.amount) || 0;
      if (amount === 0) return;

      if (!Object.prototype.hasOwnProperty.call(result, accountId)) {
        result[accountId] = 0;
      }

      if (txn.status === 'deposit') {
        result[accountId] += amount;
      } else if (txn.status === 'withdraw') {
        result[accountId] -= amount;
      }
    });

    return result;
  }, [banks, transactions]);

  const totalBalance = useMemo(
    () =>
      banks.reduce((sum, bank) => {
        const base = bank.id != null ? bankBalanceMap[bank.id] : Number(bank.beginningBalance || 0);
        const amount = Number(base || 0);
        return sum + (Number.isNaN(amount) ? 0 : amount);
      }, 0),
    [banks, bankBalanceMap]
  );

  const buildWhatsAppMessage = (bank) => {
    const lines = [
      `*Bank:* ${bank.name || 'N/A'}`,
      `*Account Holder:* ${bank.accountHolderName || 'N/A'}`,
      `*Account Number:* ${bank.accountNumber || 'N/A'}`
    ];

    return lines.join('\n');
  };

  const copyBankDetails = async (bank) => {
    const message = buildWhatsAppMessage(bank);

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(message);
      } else {
        throw new Error('Clipboard API unavailable');
      }

      Swal.fire({
        title: 'Copied',
        text: 'Bank details are ready to paste into WhatsApp.',
        icon: 'success',
        timer: 1600,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      Swal.fire({
        title: 'Copy failed',
        text: 'Clipboard was blocked. Please copy manually.',
        icon: 'error'
      });
    }
  };

  const fetchBanks = async () => {
    try {
      setError(null);
      const response = await getBanks();
      const data = response?.data?.data || [];
      setBanks(data);
    } catch (err) {
      setError('Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchBankTransactions = async () => {
    try {
      setTransactionsError(null);
      const response = await getBankTransactions();
      const data = response?.data?.data || [];
      setTransactions(data);
    } catch (err) {
      setTransactionsError('Failed to load bank transactions');
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
    fetchBankTransactions();
  }, []);

  const resetForm = () => {
    setName('');
    setAccountHolderName('');
    setAccountNumber('');
    setBeginningBalance('0');
    setFormError(null);
  };

  const handleOpenModal = () => {
    resetForm();
    setEditingBank(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (bank) => {
    setName(bank.name || '');
    setAccountHolderName(bank.accountHolderName || '');
    setAccountNumber(bank.accountNumber || '');
    setBeginningBalance(bank.beginningBalance?.toString() || '0');
    setFormError(null);
    setEditingBank(bank);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setIsModalOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedAccountHolderName = accountHolderName.trim();
    const trimmedAccountNumber = accountNumber.trim();
    const balanceValue = Number(beginningBalance);

    if (!trimmedName || !trimmedAccountHolderName || !trimmedAccountNumber || Number.isNaN(balanceValue)) {
      setFormError('Bank name, account holder, account number, and a numeric beginning balance are required');
      return;
    }

    setFormError(null);
    setSaving(true);

    const payload = {
      name: trimmedName,
      accountHolderName: trimmedAccountHolderName,
      accountNumber: trimmedAccountNumber,
      beginningBalance: balanceValue
    };

    try {
      if (editingBank) {
        await updateBank(editingBank.id, payload);
      } else {
        await createBank(payload);
      }

      await fetchBanks();
      setIsModalOpen(false);
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to save bank account';
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const filteredBanks = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return banks;
    }

    const includesTerm = (value) => String(value ?? '').toLowerCase().includes(term);

    return banks.filter((bank) => {
      const nameMatch = includesTerm(bank.name);
      const holderMatch = includesTerm(bank.accountHolderName);
      const accountMatch = includesTerm(bank.accountNumber);
      const balanceMatch = includesTerm(bank.beginningBalance);
      return nameMatch || holderMatch || accountMatch || balanceMatch;
    });
  }, [banks, searchTerm]);

  const handleDelete = async (bank) => {
    const result = await Swal.fire({
      title: 'Delete bank?',
      text: `This will remove "${bank.name}" permanently.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#b91c1c',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      await deleteBank(bank.id);
      await fetchBanks();
      await Swal.fire({
        title: 'Deleted',
        text: 'Bank account was removed.',
        icon: 'success',
        timer: 1200,
        showConfirmButton: false
      });
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to delete bank account';
      Swal.fire({
        title: 'Error',
        text: message,
        icon: 'error'
      });
    }
  };

  const formatCreatedDate = (value) => {
    if (!value) {
      return 'Recently added';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'Recently added';
    }

    return date.toLocaleDateString();
  };

  const formatDate = (value) => {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleOpenBankDetails = (bank) => {
    setDetailBank(bank);
  };

  const handleCloseBankDetails = () => {
    setDetailBank(null);
  };

  const selectedBankTransactions = useMemo(() => {
    if (!detailBank || !detailBank.id) {
      return [];
    }

    const accountId = detailBank.id;

    return [...transactions]
      .filter((txn) => txn.bankAccountId === accountId)
      .sort((a, b) => {
        const timeA = a.date ? new Date(a.date).getTime() : 0;
        const timeB = b.date ? new Date(b.date).getTime() : 0;
        if (timeA === timeB) {
          return (b.id || 0) - (a.id || 0);
        }
        return timeB - timeA;
      });
  }, [detailBank, transactions]);

  return (
    <PageSection>
      <Header>
        <div>
          <h2 style={{ margin: 0, color: '#0f172a' }}>Bank accounts</h2>
          <Intro>Track cash positions, account numbers, and initial balances.</Intro>
        </div>
        <ControlsRow>
          <SearchInput
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by bank, holder, or account number"
          />
          <AddButton type="button" onClick={handleOpenModal}>
            + Add bank
          </AddButton>
        </ControlsRow>
      </Header>

      <TotalBalanceCard>
        <span>Total current bank balance</span>
        <span>LKR {formatBalance(totalBalance)}</span>
      </TotalBalanceCard>

      {loading && <p>Loading bank accounts...</p>}
      {error && !loading && <p style={{ color: '#dc2626' }}>{error}</p>}

      {!loading && !error && (
        <>
          {banks.length > 0 ? (
            <Table>
              <TableHead>
                <span>Bank details</span>
                <span>Account holder</span>
                <span>Account number</span>
                <span>Current balance</span>
                <span style={{ textAlign: 'right' }}>Actions</span>
              </TableHead>
              <TableBody>
                {filteredBanks.length > 0 ? (
                  filteredBanks.map((bank) => (
                    <TableRow key={bank.id || bank.accountNumber}>
                      <TableCell>
                        <BankName>{bank.name}</BankName>
                        <span style={{ color: '#94a3b8', fontSize: 12 }}>
                          Added {formatCreatedDate(bank.createdAt || bank.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <AccountHolder>{bank.accountHolderName || '—'}</AccountHolder>
                      </TableCell>
                      <TableCell>
                        <AccountNumber>{bank.accountNumber}</AccountNumber>
                      </TableCell>
                      <TableCell>
                        <CurrencyText>{formatBalance(bankBalanceMap[bank.id] ?? bank.beginningBalance)}</CurrencyText>
                        <span style={{ fontSize: 12, color: '#64748b' }}>
                          Beginning: LKR {formatBalance(bank.beginningBalance)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <ActionsCell>
                          <ActionButton type="button" onClick={() => handleOpenBankDetails(bank)}>
                            View
                          </ActionButton>
                          <CopyButton type="button" onClick={() => copyBankDetails(bank)}>
                            Copy
                          </CopyButton>
                          <ActionButton type="button" onClick={() => handleOpenEditModal(bank)}>
                            Edit
                          </ActionButton>
                          <DangerButton type="button" onClick={() => handleDelete(bank)}>
                            Delete
                          </DangerButton>
                        </ActionsCell>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <EmptyRow>No bank accounts match your search.</EmptyRow>
                )}
              </TableBody>
            </Table>
          ) : (
            <EmptyPlaceholder>No bank accounts yet. Add your first institution.</EmptyPlaceholder>
          )}
        </>
      )}

      {isModalOpen && (
        <ModalBackdrop onClick={handleCloseModal}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalTitle>{editingBank ? 'Edit bank account' : 'Add bank account'}</ModalTitle>
            <ModalSubtitle>Store institution name, account holder, account number, and starting balance.</ModalSubtitle>

            <form onSubmit={handleSubmit}>
              <FormField>
                <Label htmlFor="bank-name">Bank name</Label>
                <Input
                  id="bank-name"
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="e.g. First National Bank"
                />
              </FormField>

              <FormField>
                <Label htmlFor="bank-holder">Account holder name</Label>
                <Input
                  id="bank-holder"
                  type="text"
                  value={accountHolderName}
                  onChange={(event) => setAccountHolderName(event.target.value)}
                  placeholder="e.g. Priyantha Perera"
                />
              </FormField>

              <FormField>
                <Label htmlFor="bank-account">Account number</Label>
                <Input
                  id="bank-account"
                  type="text"
                  value={accountNumber}
                  onChange={(event) => setAccountNumber(event.target.value)}
                  placeholder="XXXX-XXXX-XXXX"
                />
              </FormField>

              <FormField>
                <Label htmlFor="bank-balance">Balance (LKR)</Label>
                <Input
                  id="bank-balance"
                  type="number"
                  step="0.01"
                  value={beginningBalance}
                  onChange={(event) => setBeginningBalance(event.target.value)}
                  placeholder="0.00"
                />
              </FormField>

              {formError && <ErrorText>{formError}</ErrorText>}

              <ModalActions>
                <SecondaryButton type="button" onClick={handleCloseModal} disabled={saving}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={saving}>
                  {saving ? 'Saving…' : editingBank ? 'Update bank' : 'Save bank'}
                </PrimaryButton>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalBackdrop>
      )}

      {detailBank && (
        <ModalBackdrop onClick={handleCloseBankDetails}>
          <ModalCard onClick={(event) => event.stopPropagation()} style={{ maxWidth: 860 }}>
            <ModalTitle>{detailBank.name || 'Bank account'}</ModalTitle>
            <ModalSubtitle>View all transactions recorded for this bank account.</ModalSubtitle>

            <div style={{ marginBottom: 10, fontSize: 13, color: '#64748b' }}>
              <div>
                <span>Account holder: </span>
                <strong style={{ color: '#0f172a' }}>{detailBank.accountHolderName || '—'}</strong>
              </div>
              <div>
                <span>Account number: </span>
                <strong style={{ color: '#0f172a' }}>{detailBank.accountNumber || '—'}</strong>
              </div>
              <div>
                <span>Current balance: </span>
                <strong style={{ color: '#0f172a' }}>
                  LKR {formatBalance(bankBalanceMap[detailBank.id] ?? detailBank.beginningBalance)}
                </strong>
              </div>
            </div>

            <div style={{ maxHeight: 360, overflow: 'auto', marginBottom: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '6px 4px' }}>Date</th>
                    <th style={{ padding: '6px 4px' }}>Name</th>
                    <th style={{ padding: '6px 4px' }}>Amount</th>
                    <th style={{ padding: '6px 4px' }}>Type</th>
                    <th style={{ padding: '6px 4px' }}>Cash?</th>
                    <th style={{ padding: '6px 4px' }}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {transactionsLoading ? (
                    <tr>
                      <td colSpan={6} style={{ padding: '12px 4px', textAlign: 'center', color: '#94a3b8' }}>
                        Loading transactions...
                      </td>
                    </tr>
                  ) : transactionsError ? (
                    <tr>
                      <td colSpan={6} style={{ padding: '12px 4px', textAlign: 'center', color: '#dc2626' }}>
                        {transactionsError}
                      </td>
                    </tr>
                  ) : selectedBankTransactions.length > 0 ? (
                    selectedBankTransactions.map((txn) => {
                      const amount = Number(txn.amount) || 0;
                      const isDeposit = txn.status === 'deposit';
                      const isWithdraw = txn.status === 'withdraw';
                      const prefix = isDeposit ? '+' : isWithdraw ? '-' : '';
                      const color = isDeposit ? '#15803d' : isWithdraw ? '#b91c1c' : '#0f172a';

                      return (
                        <tr key={txn.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '6px 4px', whiteSpace: 'nowrap' }}>{formatDate(txn.date)}</td>
                          <td style={{ padding: '6px 4px' }}>{txn.name}</td>
                          <td style={{ padding: '6px 4px', whiteSpace: 'nowrap', color }}>
                            {`${prefix}LKR ${formatBalance(Math.abs(amount))}`}
                          </td>
                          <td style={{ padding: '6px 4px', textTransform: 'capitalize' }}>{txn.status}</td>
                          <td style={{ padding: '6px 4px' }}>{txn.isFromCash ? 'Yes' : 'No'}</td>
                          <td style={{ padding: '6px 4px' }}>{txn.note || '—'}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ padding: '12px 4px', textAlign: 'center', color: '#94a3b8' }}>
                        No transactions recorded for this bank account yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <ModalActions>
              <SecondaryButton type="button" onClick={handleCloseBankDetails}>
                Close
              </SecondaryButton>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      )}
    </PageSection>
  );
};

export default BankPage;
